import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { supabase, supabaseReady } from "@/lib/supabaseClient";
import { ensureProfile } from "@/services/auth/ensureProfile";
import { fetchProfile, needsCompletion, updateProfile, deriveDisplayName } from "@/services/auth/socialFlow";

export default function OAuthCallbackPage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);

  useEffect(() => {
    const req = (import.meta.env.VITE_REQUIRED_PROFILE_FIELDS || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setRequiredFields(req);
  }, []);

  useEffect(() => {
    async function load() {
      console.log("[OAUTH_CALLBACK] Initializing...");
      try {
        if (!supabaseReady) {
          console.error("[OAUTH_CALLBACK] Supabase not ready");
          throw new Error("Supabase não configurado. Verifique as variáveis de ambiente.");
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error("[OAUTH_CALLBACK] Session error:", sessionError);
          throw new Error(`Erro ao obter sessão: ${sessionError.message}`);
        }

        const u = data.session?.user;
        if (!u) {
          console.log("[OAUTH_CALLBACK] No user found, redirecting to login");
          setError("Nenhuma sessão encontrada. Tente fazer login novamente.");
          const dest = "/cadastro";
          console.log("[REDIRECT]", dest);
          setTimeout(() => window.location.replace(dest), 2000);
          return;
        }

        console.log("[OAUTH_CALLBACK] User found:", u.id);

        await ensureProfile(u);

        const p = await fetchProfile(u.id);
        console.log("[OAUTH_CALLBACK] Profile fetched:", p);

        const incomplete = needsCompletion(p);
        console.log("[OAUTH_CALLBACK] Needs completion:", incomplete);

        setUser(u);
        setProfile(p);
        setFullName(p?.full_name && p.full_name !== "—" ? p.full_name : deriveDisplayName(u));
        setPhone(p?.phone || "");
        setShowForm(incomplete);
        setLoading(false);

        if (!incomplete) {
          const dest = (import.meta.env.VITE_POST_LOGIN_REDIRECT || "/userhome").trim();
          console.log("[OAUTH_CALLBACK] Profile complete, redirecting to:", dest);
          console.log("[REDIRECT]", dest);
          window.location.replace(dest);
        }
      } catch (e: any) {
        console.error("[OAUTH_CALLBACK] Error:", e);
        const errorMsg = e.message || "Erro inesperado ao processar autenticação";
        setError(errorMsg);
        setLoading(false);
        
        setTimeout(() => {
          window.location.replace("/cadastro");
        }, 3000);
      }
    }
    load();
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    
    console.log("[OAUTH_CALLBACK] Saving profile...");
    
    try {
      if (!user) {
        console.error("[OAUTH_CALLBACK] No user found");
        throw new Error("Usuário ausente.");
      }
      
      if (!fullName.trim()) {
        throw new Error("Informe seu nome.");
      }

      if (requiredFields.includes("phone")) {
        if (!phone.trim()) throw new Error("Informe seu telefone.");
        const clean = phone.trim();
        if (!/^\+?[1-9]\d{7,14}$/.test(clean)) {
          throw new Error("Telefone inválido. Use formato internacional (+55...).");
        }
      }

      await updateProfile(user.id, { full_name: fullName.trim(), phone: phone.trim() || undefined });
      console.log("[OAUTH_CALLBACK] Profile updated successfully");
      
      const dest = (import.meta.env.VITE_POST_LOGIN_REDIRECT || "/userhome").trim();
      console.log("[OAUTH_CALLBACK] Redirecting to:", dest);
      console.log("[REDIRECT]", dest);
      window.location.replace(dest);
    } catch (err: any) {
      console.error("[OAUTH_CALLBACK] Save error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center gradient-blur-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 max-w-lg">
        <Card className="p-8 space-y-6">
          <h1 className="text-2xl font-bold">Verificação de Conta</h1>

          {loading && <p className="text-sm text-muted-foreground">Carregando...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {!loading && !error && user && showForm && (
            <form onSubmit={handleSave} className="space-y-4">
              <p className="text-sm text-muted-foreground">Complete os dados obrigatórios antes de continuar.</p>
              <div className="space-y-1">
                <label className="text-sm font-medium">Nome completo</label>
                <input
                  className="w-full rounded border px-3 py-2"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={saving}
                />
              </div>

              {requiredFields.includes("phone") && (
                <div className="space-y-1">
                  <label className="text-sm font-medium">Telefone</label>
                  <input
                    className="w-full rounded border px-3 py-2"
                    placeholder="+5511999999999"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={saving}
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full rounded bg-primary text-white py-2 font-medium disabled:opacity-60"
              >
                {saving ? "Salvando..." : "Continuar"}
              </button>
            </form>
          )}

          {!loading && !error && user && !showForm && (
            <p className="text-sm text-muted-foreground">Perfil completo. Redirecionando...</p>
          )}
        </Card>
      </div>
    </div>
  );
}
